/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import {
  type Entrance,
  type Path,
  SERVICE_REQUESTS,
} from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore, useMapControl } from '@ahm/react-map/store';
import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMutationState } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { useBookingRequestBodies } from '../hooks/use-booking-request';
import { useCreateOrder } from '../hooks/use-create-order';
import { useEstimateBookingOrder } from '../hooks/use-estimate-order';
import InsuranceItem from './insurance/insurance-item';
import PaymentSelection from './payment/payment-selection';
import PickupTimeSelection from './pickup-time/pickup-time-selection';
import PromoSelection from './promotion/promo-selection';
import SurgeDownIcon from '@/assets/icons/surge-down-icon.svg';
import SurgeUpIcon from '@/assets/icons/surge-up-icon.svg';
import InfoTooltip from '@/components/info-tooltip';
import ModalWrapper from '@/components/modals/modal-wrapper';
import PaymentDetailsModal from '@/components/modals/payment-details-modal';
import TruckSuggestAlert from '@/features/booking/components/truck-suggest-alert';
import { useOptimizedRouteHook, useRemoteConfigHook } from '@/hooks';
import { useFillData } from '@/hooks/use-fill-data';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function BookingConfirmOrder() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const [paramPromo] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const pathname = usePathname();
  const tBooking = useTranslations('booking');
  const isMobile = useIsMobile();
  const paths = useLocationStore.use.paths();
  const mapFullscreen = useMapControl.use.mapFullscreen();
  const {
    selectedService,
    estimatedData,
    setEstimatedData,
    paymentMethod: selectedPaymentMethod,
    serviceRequests: requests,
    isLoadingStore,
    isSuggestionTruckServices,
    bikePackageWeight,
    itemDescriptions,
    replacedEntrances,
  } = useBookingStore((state) => state);

  const nearbyEntrances = useMapControl((state) => state.nearbyEntrances);

  const { isTruckService, isBikeService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );
  const fakeDisabled = useMemo(() => {
    if (
      (isBikeService && !bikePackageWeight) ||
      (isTruckService && !itemDescriptions.length)
    ) {
      return true;
    }
    return false;
  }, [isBikeService, bikePackageWeight, isTruckService, itemDescriptions]);

  const { tspOptimizeBody, mutateOptimizeRoutesTsp } = useOptimizedRouteHook();
  const { createRequestList } = useBookingRequestBodies(pathname, paramPromo);
  const { handleFillData } = useFillData();

  const { surgePriceConfig } = useRemoteConfigHook();

  const surgeIcon = useMemo(() => {
    if (!surgePriceConfig || estimatedData?.error) return null;

    for (const item of surgePriceConfig) {
      if (item.delivery_type === selectedService?.delivery_type) {
        const estimatedSurgeRate = estimatedData?.data?.surge_rate;

        if (!estimatedSurgeRate) return null;

        if (
          item.surge_rate_decrease &&
          estimatedSurgeRate < item.surge_rate_decrease
        ) {
          return <SurgeDownIcon />;
        }

        if (
          item.surge_rate_increase &&
          estimatedSurgeRate > item.surge_rate_increase
        ) {
          return <SurgeUpIcon />;
        }
      }
    }

    return null;
  }, [
    surgePriceConfig,
    estimatedData?.error,
    estimatedData?.data?.surge_rate,
    selectedService,
  ]);

  const estimatedError = useMemo(() => {
    return Boolean(estimatedData?.error ?? !estimatedData);
  }, [estimatedData]);

  const estimatedTotalPrice = useMemo(() => {
    return estimatedData?.data?.total_price;
  }, [estimatedData]);

  const mutationStatus = useMutationState({
    filters: { status: 'pending' },
    select: (mutation) => mutation.state.status,
  });

  const loading = useMemo(() => {
    return mutationStatus?.length > 0;
  }, [mutationStatus]);

  useEffect(() => {
    void handleFillData();
  }, [handleFillData]);

  useEffect(() => {
    const debouncedOptimize = debounce(() => {
      if (!tspOptimizeBody) return;
      void mutateOptimizeRoutesTsp(tspOptimizeBody);
    }, 500);

    debouncedOptimize();

    // Cleanup function
    return () => {
      debouncedOptimize.cancel();
    };
  }, [mutateOptimizeRoutesTsp, tspOptimizeBody]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const { estimateRequestBody, createRequestBody } = useBookingRequestBodies(
    pathname,
    paramPromo
  );

  useEstimateBookingOrder(estimateRequestBody, pathname);

  const handleCreateOrder = useCreateOrder(
    createRequestBody,
    pathname,
    selectedPaymentMethod,
    paramPromo,
    isMobile
  );

  const priceOrigin = useMemo(() => {
    return estimatedData?.data?.subtotal_price &&
      estimatedData.data.subtotal_price !== estimatedData.data.total_price &&
      estimatedData.data.voucher_discount > 0
      ? formatCurrency(estimatedData?.data?.subtotal_price)
      : null;
  }, [estimatedData]);

  // tìm path có lat, lng trùng với replacedEntrances item có enabled = false
  const findPathIndexForMarkers = (
    markers: (Entrance & { enabled?: boolean })[],
    paths: Path[]
  ) => {
    if (!markers || !paths || markers.length === 0) return -1;

    return paths.findIndex((path) => {
      return markers.some((marker) => {
        return (
          path.lat === marker.coordinates[0] &&
          path.lng === marker.coordinates[1] &&
          marker.enabled === false
        );
      });
    });
  };

  const pathIndexRestricted = useMemo(() => {
    if (!nearbyEntrances || nearbyEntrances.length === 0) return -1;
    return findPathIndexForMarkers(nearbyEntrances, paths);
  }, [nearbyEntrances, paths]);

  const disableCreateOrder = useMemo(() => {
    if (paths.length < 2 || !selectedService?._id || pathIndexRestricted >= 0)
      return true;

    return false;
  }, [paths.length, selectedService?._id, pathIndexRestricted]);

  useEffect(() => {
    if (paths.length < 2) {
      setEstimatedData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths]);

  const enableInsuranceService = useMemo(() => {
    const requestList = createRequestList(requests || {}, selectedService?._id);
    return requestList.some((req) =>
      req._id.includes(SERVICE_REQUESTS.INSURANCE)
    );
  }, [requests, selectedService, createRequestList]);

  if (isTruckService && (paths.length < 2 || !selectedService?._id))
    return null;

  return (
    <>
      <Card
        left="var(--_main-padding-left, calc(var(--booking-layout-navbar-offset, 0px) + var(--_main-padding)))"
        right="var(--_main-padding-right, calc(var(--booking-layout-aside-offset, 0px) + var(--_main-padding)))"
        radius={0}
        pos="fixed"
        bottom={0}
        p={0}
        shadow="buttonDock"
        styles={{
          root: {
            zIndex: 30,
            borderTopLeftRadius: rem(12),
            borderTopRightRadius: rem(12),
            overflow: mapFullscreen ? 'hidden' : 'visible',
          },
        }}
      >
        {isMobile ? (
          <Stack px="md" pt={12} pb="lg" gap="xs">
            {/* {enableInsuranceService || estimatedData ? <InsuranceItem /> : null} */}
            {estimatedData ? <InsuranceItem /> : null}

            <Group wrap="nowrap" gap={4}>
              <PickupTimeSelection />
              <Divider h={32} w={1} bg="neutral.3" />
              <Box flex="none">
                <PromoSelection />
              </Box>
              <Divider h={32} w={1} bg="neutral.3" />
              <PaymentSelection />
            </Group>
            <Divider />
            <Group justify="space-between">
              <Group wrap="nowrap" gap={4}>
                <Text size="sm" fw={700} c="neutral.6">
                  {tBooking('billing.total_fee')}
                </Text>
                {estimatedData?.data ? (
                  <InfoTooltip
                    label={tBooking('billing.billing_detail')}
                    onClick={open}
                  />
                ) : null}
              </Group>
              <Group wrap="nowrap">
                {isLoadingStore || loading ? (
                  <Skeleton my={rem(6)} h={rem(20)} w={rem(112)} />
                ) : (
                  <>
                    {priceOrigin ? (
                      <Text fz={14} td="line-through" c="neutral.5" fw={500}>
                        {priceOrigin}
                      </Text>
                    ) : null}
                    <Title
                      order={3}
                      c={estimatedTotalPrice ? 'primary.6' : 'neutral.4'}
                      fz={rem(20)}
                      lh={rem(32)}
                      className="flex flex-row flex-nowrap items-center gap-1"
                    >
                      <span className="truncate">
                        {typeof estimatedTotalPrice === 'number'
                          ? formatCurrency(estimatedTotalPrice)
                          : '-đ'}
                      </span>
                    </Title>
                  </>
                )}
              </Group>
            </Group>
            <Button
              mt={8}
              size="md"
              h={rem(48)}
              fullWidth
              data-disabled={fakeDisabled}
              loading={isLoadingStore || loading}
              disabled={!!estimatedError || pathIndexRestricted >= 0}
              onClick={handleCreateOrder}
            >
              {tBooking('create_order')}
            </Button>
          </Stack>
        ) : (
          <Stack p="md" gap="md">
            {isSuggestionTruckServices && isBikeService ? (
              <TruckSuggestAlert />
            ) : null}
            {/* {enableInsuranceService || estimatedData ? <InsuranceItem /> : null} */}
            {estimatedData ? <InsuranceItem /> : null}

            <SimpleGrid cols={3} spacing={rem(8)}>
              <PickupTimeSelection />

              <PromoSelection />

              <PaymentSelection />
            </SimpleGrid>
            <Divider />
            <Group wrap="nowrap" gap={rem(8)} align="center">
              <Stack gap={0} ta="left" flex={1}>
                <Text size="sm" fw={500} c="neutral.7">
                  {tBooking('billing.total_fee')}
                </Text>
                <Stack mih={40} gap={0}>
                  {isLoadingStore ? (
                    <Skeleton my="xs" h={rem(20)} w={115} />
                  ) : (
                    <Group wrap="nowrap" gap={4}>
                      {surgeIcon}
                      <Title
                        order={3}
                        pt={rem(4)}
                        c={'primary.6'}
                        fz={rem(20)}
                        lh={rem(32)}
                        className="flex flex-row flex-nowrap items-center gap-1"
                      >
                        <span className="truncate">
                          {typeof estimatedTotalPrice === 'number'
                            ? formatCurrency(estimatedTotalPrice)
                            : '-đ'}
                        </span>
                        {estimatedData?.data ? (
                          <InfoTooltip
                            label={tBooking('billing.billing_detail')}
                            onClick={open}
                          />
                        ) : null}
                      </Title>

                      {priceOrigin ? (
                        <Text fz={14} td="line-through" c="neutral.5" fw={500}>
                          {priceOrigin}
                        </Text>
                      ) : null}
                    </Group>
                  )}
                </Stack>
              </Stack>
              <Button
                size="md"
                h={rem(48)}
                w={rem(200)}
                data-disabled={fakeDisabled}
                loading={isLoadingStore}
                disabled={disableCreateOrder || !!estimatedError}
                onClick={handleCreateOrder}
              >
                {tBooking('create_order')}
              </Button>
            </Group>
          </Stack>
        )}
      </Card>
      <ModalWrapper
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
        title={tBooking('billing.bill_title')}
        isOpen={isOpen}
        onClose={close}
      >
        <PaymentDetailsModal onClose={close} />
      </ModalWrapper>
    </>
  );
}
